import React, { useMemo } from 'react';
import { Trophy, Flag, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { detectSessionType } from '../utils/sessionType';
import PdfShareActions from './PdfShareActions';

// Format seconds → M:SS.mmm (or SS.mmm sub-minute) with 3-digit ms.
const fmtTime = (secs) => {
  if (secs == null || !Number.isFinite(secs) || secs <= 0) return '—';
  const m = Math.floor(secs / 60);
  const rem = secs - m * 60;
  const secStr = rem.toFixed(3).padStart(6, '0');
  return m > 0 ? `${m}:${secStr}` : `${rem.toFixed(3)}s`;
};

// Format gap-to-leader in +/− M:SS.mmm — same styling as the live panel.
const fmtGap = (secs) => {
  if (secs == null || !Number.isFinite(secs)) return '—';
  if (Math.abs(secs) < 0.0005) return '—';
  const abs = Math.abs(secs);
  const m = Math.floor(abs / 60);
  const rem = abs - m * 60;
  const secStr = rem.toFixed(3).padStart(6, '0');
  return `+${m}:${secStr}`;
};

// Derive a "state" tag for the finish list. Deliberately conservative —
// we only mark a driver as DNF if their laps count is materially below
// the winner's, so a driver that finished the last lap late still gets
// a clean "Terminó" tag.
const stateOf = (driver, winnerLaps) => {
  const l = driver.laps || 0;
  if (winnerLaps >= 3 && l < Math.floor(winnerLaps / 2)) return 'DNF';
  if (winnerLaps >= 1 && l === 0) return 'No largó';
  return 'Terminó';
};

const bestLapOf = (d) => {
  const b = Number(d.best_lap);
  if (Number.isFinite(b) && b > 0) return b;
  const valid = (d.lap_times || []).map(Number).filter((t) => Number.isFinite(t) && t > 0);
  return valid.length ? Math.min(...valid) : Infinity;
};

const byBestLap = (a, b) =>
  (a.best_lap_effective - b.best_lap_effective) || (b.laps_effective - a.laps_effective);

const byRacePosition = (a, b) =>
  (b.laps_effective - a.laps_effective) || (a.total_time_effective - b.total_time_effective);

const ProvisionalResults = ({ race }) => {
  const sessionType = useMemo(() => detectSessionType(race), [race]);
  const isBestLap = sessionType === 'best_lap';
  const rows = useMemo(() => {
    if (!race || !Array.isArray(race.drivers)) return [];
    // Sort by laps desc, then by total_time asc. Missing total_time is
    // treated as +Infinity so drivers without a valid total go to the
    // bottom of a laps-tied group without polluting ordering.
    const enriched = race.drivers
      .map((d) => {
        // Laps count precedence:
        //   1. `driver.laps` when the backend provided it (AutoLive
        //      persists Speedhive's ls counter, FINALVTAS parser
        //      persists max(lap_number) so lap 1 is counted).
        //   2. max(lap_details.lap_number) — same source of truth as
        //      the timing sheet, corrects for XLS variants where the
        //      standing-start lap 1 isn't recorded as a row.
        //   3. lap_times.length — last-resort fallback for legacy docs.
        const lapArr = Array.isArray(d.lap_times) ? d.lap_times : [];
        const details = Array.isArray(d.lap_details) ? d.lap_details : [];
        const maxLapNum = details.reduce(
          (mx, l) => (Number.isFinite(Number(l?.lap_number)) && Number(l.lap_number) > mx ? Number(l.lap_number) : mx),
          0,
        );
        let laps = 0;
        if (Number.isFinite(Number(d.laps)) && Number(d.laps) > 0) {
          laps = Number(d.laps);
        } else if (maxLapNum > 0) {
          laps = maxLapNum;
        } else {
          laps = lapArr.length;
        }
        // Total time precedence:
        //   1. `driver.total_time` (backend-computed; includes the
        //      standing-start lap).
        //   2. Sum of `lap_times` — will under-estimate on races
        //      whose lap 1 isn't recorded, but stays consistent
        //      across drivers so gap ordering doesn't flip.
        let total = Number(d.total_time);
        if (!Number.isFinite(total) || total <= 0) {
          total = lapArr.reduce((acc, t) => {
            const n = Number(t);
            return acc + (Number.isFinite(n) && n > 0 ? n : 0);
          }, 0);
        }
        return {
          ...d,
          laps_effective: laps,
          total_time_effective: total > 0 ? total : Infinity,
          best_lap_effective: bestLapOf(d),
        };
      })
      .sort(isBestLap ? byBestLap : byRacePosition);
    return enriched;
  }, [race, isBestLap]);

  if (!rows.length) {
    return (
      <Card className="bg-neutral-900 border-red-900/30">
        <CardContent className="py-16 text-center">
          <Trophy className="w-16 h-16 mx-auto text-gray-600 mb-4" />
          <p className="text-gray-400">Sin pilotos para armar el resultado.</p>
        </CardContent>
      </Card>
    );
  }

  const winnerLaps = rows[0].laps_effective || 0;
  const winnerTotal = rows[0].total_time_effective;
  const winnerBest = rows[0].best_lap_effective;

  const gapAndState = (driver, idx) => {
    let gapLabel = '—';
    if (idx === 0) {
      gapLabel = '';
    } else if (isBestLap) {
      gapLabel = Number.isFinite(driver.best_lap_effective) && Number.isFinite(winnerBest)
        ? fmtGap(driver.best_lap_effective - winnerBest) : '—';
    } else if ((driver.laps_effective || 0) < winnerLaps) {
      const diff = winnerLaps - (driver.laps_effective || 0);
      gapLabel = `+${diff} vta${diff !== 1 ? 's' : ''}`;
    } else if (Number.isFinite(driver.total_time_effective) && Number.isFinite(winnerTotal)) {
      gapLabel = fmtGap(driver.total_time_effective - winnerTotal);
    }
    const state = isBestLap
      ? (Number.isFinite(driver.best_lap_effective) ? 'Terminó' : 'Sin tiempo')
      : stateOf({ ...driver, laps: driver.laps_effective }, winnerLaps);
    return { gapLabel, state };
  };

  const pdfRows = () => rows.map((d, idx) => {
    const { gapLabel, state } = gapAndState(d, idx);
    return {
      pos: idx + 1,
      number: d.driver_number,
      name: d.driver_name,
      laps: d.laps_effective || 0,
      best: fmtTime(d.best_lap_effective),
      gap: gapLabel,
      state,
    };
  });

  return (
    <div className="space-y-4" data-testid="provisional-results-view">
      {/* Disclaimer banner — resultado provisorio, no oficial. */}
      <div className="rounded-md border border-yellow-600/40 bg-yellow-500/10 px-4 py-3 flex items-start gap-2">
        <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-semibold text-yellow-200">Resultado provisorio</p>
          <p className="text-yellow-100/70 text-xs">
            Este resultado se calcula automáticamente con los datos capturados
            ({isBestLap ? 'mejor vuelta de cada piloto' : 'vueltas completadas + tiempo total'}).
            Puede diferir del acta oficial hasta que el organizador la publique.
          </p>
        </div>
      </div>

      <Card className="bg-neutral-900 border-red-900/30">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Trophy className="w-5 h-5 text-red-500" />
            Clasificación provisoria
            <Badge
              className="ml-1 bg-neutral-800 text-gray-300 border border-neutral-700 text-[10px] uppercase tracking-wider"
              data-testid="prov-session-type-badge"
            >
              {isBestLap ? 'Por mejor vuelta' : 'Por posición de carrera'}
            </Badge>
          </CardTitle>
          <CardDescription className="text-gray-400" data-testid="prov-sort-description">
            {isBestLap
              ? 'Orden por mejor vuelta (de menor a mayor). Diferencia contra la mejor vuelta del primero.'
              : 'Orden por vueltas completadas y tiempo total. Diferencia calculada contra el líder.'}
          </CardDescription>
          <PdfShareActions race={race} isBestLap={isBestLap} getRows={pdfRows} />
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-800">
                  <th className="text-left py-3 px-3 text-gray-300 font-semibold w-14">Pos</th>
                  <th className="text-left py-3 px-3 text-gray-300 font-semibold w-14">#</th>
                  <th className="text-left py-3 px-3 text-gray-300 font-semibold">Piloto</th>
                  <th className="text-right py-3 px-3 text-gray-300 font-semibold">Vtas</th>
                  <th className="text-right py-3 px-3 text-gray-300 font-semibold">Mejor Vuelta</th>
                  <th className="text-right py-3 px-3 text-gray-300 font-semibold">Diferencia</th>
                  <th className="text-center py-3 px-3 text-gray-300 font-semibold">Estado</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((driver, idx) => {
                  const pos = idx + 1;
                  const isPodium = pos <= 3;
                  const isLeader = pos === 1;
                  const { gapLabel, state } = gapAndState(driver, idx);
                  return (
                    <tr
                      key={driver.driver_number}
                      className={`border-b border-neutral-800 hover:bg-red-950/20 ${isLeader ? 'bg-yellow-500/5' : ''}`}
                      data-testid={`prov-result-row-${driver.driver_number}`}
                    >
                      <td className="py-3 px-3 font-medium">
                        <span
                          className={`inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded text-xs font-bold ${
                            isLeader
                              ? 'bg-yellow-500/25 text-yellow-300 border border-yellow-500/50'
                              : isPodium
                                ? 'bg-neutral-700/40 text-gray-100 border border-neutral-600'
                                : 'text-gray-300'
                          }`}
                        >
                          P{pos}
                        </span>
                      </td>
                      <td className="py-3 px-3 font-bold text-white">#{driver.driver_number}</td>
                      <td className="py-3 px-3 text-white truncate max-w-[280px]">{driver.driver_name}</td>
                      <td className="py-3 px-3 text-right text-white font-mono">{driver.laps_effective || 0}</td>
                      <td className="py-3 px-3 text-right text-white font-mono">{fmtTime(driver.best_lap_effective)}</td>
                      <td className="py-3 px-3 text-right font-mono text-red-400">{gapLabel}</td>
                      <td className="py-3 px-3 text-center">
                        {state === 'Terminó' ? (
                          <Badge className="bg-green-600/20 text-green-300 border border-green-500/40">
                            <Flag className="w-3 h-3 mr-1" />
                            Terminó
                          </Badge>
                        ) : (
                          <Badge className="bg-neutral-700/40 text-gray-300 border border-neutral-600">
                            {state}
                          </Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProvisionalResults;
